import I18n from 'I18n';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { BulletListIcon } from 'foundations-assets/icons/components/BulletListIcon';
import { CoachingPlaylistIcon } from 'foundations-assets/icons/components/CoachingPlaylistIcon';
import { GiftIcon } from 'foundations-assets/icons/components/GiftIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogHeaderImage } from 'foundations-components/transitional/components/DialogHeaderImage';
import { IconCircle } from 'foundations-components/transitional/components/IconCircle';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { Modal } from 'foundations-components/transitional/components/Modal';
import Products from 'merchandise-lib/constants/Product';
import { PROFESSIONAL } from 'self-service-api/constants/Editions';
import { DEFAULT_TRIAL_DATA_EXPIRATION_DAYS, DEFAULT_TRIAL_DAYS } from '../../../../constants/trial';
import { getIconForBullet } from '../../../../utils/icons';
import { getHubAndTierFromApiName, getLowerTierProduct } from '../../../../utils/product';
import { addDaysToDate } from '../../../../utils/trials';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BulletPoint = ({
  icon,
  backgroundColor,
  message
}) => /*#__PURE__*/_jsxs(Flex, {
  alignItems: "start",
  gap: "300",
  marginBlockEnd: "300",
  children: [/*#__PURE__*/_jsx(IconCircle, {
    icon: icon,
    backgroundColor: backgroundColor,
    size: "300",
    borderWidth: "0",
    legacy: true,
    flexShrink: 0
  }), /*#__PURE__*/_jsx(Text, {
    marginBlockStart: "100",
    children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: message
    })
  })]
});
export const TrialActivationModal = ({
  hidePrompt,
  apiName,
  featureDisplayName,
  onContinueClick,
  isPaidPortal
}) => {
  // Check if this is a marketing-plus bundle which has additional feature bullet
  const isMarketingPlusBundle = apiName === Products.MARKETING_PLUS_SUITE_BUNDLE_PROFESSIONAL || apiName === Products.MARKETING_PLUS_SUITE_BUNDLE_ENTERPRISE;
  const tier = getHubAndTierFromApiName(apiName).tier;
  const tierTranslationKey = tier === PROFESSIONAL ? I18n.text('purchaseMotions.products.hubspotProfessional').toLocaleLowerCase() : I18n.text('purchaseMotions.products.enterprise').toLocaleLowerCase();
  const lowerTierProduct = getLowerTierProduct(apiName);
  const expirationDate = addDaysToDate(new Date(), DEFAULT_TRIAL_DATA_EXPIRATION_DAYS);
  const formattedExpirationDate = /*#__PURE__*/_jsx(Text, {
    typography: "heading-50",
    as: "strong",
    children: /*#__PURE__*/_jsx(FormattedDateTime, {
      value: expirationDate,
      format: "ll",
      type: "userTz"
    })
  });
  const bodyMessage = () => {
    if (apiName === Products.COMMERCE_HUB_PROFESSIONAL || apiName === Products.CRM_SUITE_STARTER_BUNDLE) {
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "purchaseMotions.trial.trial-activation-modal-redesign.body-free-portal-b",
        options: {
          numberOfDays: DEFAULT_TRIAL_DAYS
        }
      });
    }
    if (!lowerTierProduct) {
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "purchaseMotions.trial.trial-activation-modal-redesign.body"
      });
    }
    if (isPaidPortal) {
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "purchaseMotions.trial.trial-activation-modal-redesign.body-paid-portal",
        options: {
          productName: lowerTierProduct
        }
      });
    }
    return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "purchaseMotions.trial.trial-activation-modal-redesign.body-free-portal-a",
      options: {
        numberOfDays: 14,
        productName: lowerTierProduct
      }
    });
  };
  return /*#__PURE__*/_jsxs(Modal, {
    centered: true,
    use: "success",
    width: 520,
    "data-test-id": "trial-activation-modal",
    children: [/*#__PURE__*/_jsx(DialogHeaderImage, {
      offsetBottom: 10,
      offsetTop: 40,
      children: /*#__PURE__*/_jsx(Illustration, {
        name: "setup-complete",
        width: 118
      })
    }), /*#__PURE__*/_jsxs(DialogHeader, {
      marginBlock: "0",
      paddingBlockStart: "0",
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h2",
        typography: "heading-600",
        textAlign: "center",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `purchaseMotions.trial.trialActivationModal.welcome.${apiName}`
        })
      }), hidePrompt && /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: hidePrompt
      })]
    }), /*#__PURE__*/_jsxs(DialogBody, {
      textAlign: "left",
      children: [/*#__PURE__*/_jsx(Text, {
        marginBlockEnd: "400",
        textAlign: "left",
        children: bodyMessage()
      }), /*#__PURE__*/_jsx(BulletPoint, {
        icon: getIconForBullet(apiName, 0),
        backgroundColor: "fill-accent-neutral-subtle-alt",
        message: `purchaseMotions.trial.trial-activation-modal-redesign.${apiName}.hub-features-bullet`
      }), isMarketingPlusBundle && /*#__PURE__*/_jsx(BulletPoint, {
        icon: getIconForBullet(apiName, 1),
        backgroundColor: "fill-accent-neutral-subtle-alt",
        message: `purchaseMotions.trial.trial-activation-modal-redesign.${apiName}.hub-features-bullet-2`
      }), /*#__PURE__*/_jsx(BulletPoint, {
        icon: BulletListIcon,
        backgroundColor: "fill-accent-neutral-subtle-alt",
        message: "purchaseMotions.trial.trial-activation-modal-redesign.tailored-guidance-bullet"
      }), /*#__PURE__*/_jsx(BulletPoint, {
        icon: CoachingPlaylistIcon,
        backgroundColor: "fill-accent-neutral-subtle-alt",
        message: "purchaseMotions.trial.trial-activation-modal-redesign.support-bullet"
      }), isPaidPortal ? /*#__PURE__*/_jsx(BulletPoint, {
        icon: GiftIcon,
        backgroundColor: "fill-accent-neutral-subtle-alt",
        message: "purchaseMotions.trial.trial-activation-modal-redesign.no-charge-paid-portal-bullet"
      }) : /*#__PURE__*/_jsx(BulletPoint, {
        icon: GiftIcon,
        backgroundColor: "fill-accent-neutral-subtle-alt",
        message: "purchaseMotions.trial.trial-activation-modal-redesign.no-charge-bullet"
      }), /*#__PURE__*/_jsx(Text, {
        typography: "body-100",
        textAlign: "left",
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "purchaseMotions.trial.trial-activation-modal-redesign.footer-note",
          options: {
            expirationDate: formattedExpirationDate,
            tier: tierTranslationKey
          }
        })
      })]
    }), /*#__PURE__*/_jsx(DialogFooter, {
      align: "center",
      children: /*#__PURE__*/_jsx(Button, {
        "data-test-id": "trial-activation-continue-button",
        use: "primary",
        onClick: onContinueClick,
        children: featureDisplayName ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "purchaseMotions.trial.trialActivationModal.getStarted"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "purchaseMotions.trial.trial-activation-modal-redesign.cta"
        })
      })
    })]
  });
};